<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email, minLength, sameAs } from '@vuelidate/validators';
import { isValidPassword } from 'shared/helpers/Validators';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const MIN_PASSWORD_LENGTH = 6;

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const agentPassword = ref('');
const agentPasswordConfirmation = ref('');
const selectedRoleId = ref('agent');
const currentPassword = ref('');
const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  agentPassword: {
    required,
    minLength: minLength(MIN_PASSWORD_LENGTH),
    isValidPassword,
  },
  agentPasswordConfirmation: {
    required,
    sameAsPassword: sameAs(agentPassword),
  },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  agentPassword,
  agentPasswordConfirmation,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
const currentUser = useMapGetter('getCurrentUser');

const hasLocalPassword = computed(() => {
  if (currentUser.value?.password_login_enabled === false) return false;
  if (currentUser.value?.password_login_enabled === true) return true;
  const provider = currentUser.value?.provider;
  return !provider || provider === 'email';
});

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'manager',
      name: 'manager',
      label: t('AGENT_MGMT.AGENT_TYPES.MANAGER'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const needsStepUp = computed(() => {
  if (!selectedRole.value) return false;
  if (selectedRole.value.name.startsWith('custom_')) return true;
  return selectedRole.value.name !== 'agent';
});

const showCurrentPassword = computed(
  () => needsStepUp.value && hasLocalPassword.value
);

const extractErrorMessage = error => {
  const data = error?.response?.data || {};
  const status = error?.response?.status;
  const serverMessage = data.message || data.error || '';
  const attributes = data.attributes || [];
  const attrError = data.message || '';

  if (serverMessage && /invalid current password/i.test(serverMessage)) {
    return t('AGENT_MGMT.ADD.API.INVALID_PASSWORD_MESSAGE');
  }
  if (status === 401 || status === 403) {
    return t('AGENT_MGMT.ADD.API.UNAUTHORIZED_MESSAGE');
  }
  if (status === 422 && !attributes.includes('base')) {
    return t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
  }
  if (status === 422 && attributes.includes('base') && attrError) {
    return (
      attrError || t('AGENT_MGMT.ADD.API.EXISTING_USER_PASSWORD_MESSAGE')
    );
  }
  return (
    serverMessage ||
    error?.message ||
    t('AGENT_MGMT.ADD.API.ERROR_MESSAGE')
  );
};

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  if (showCurrentPassword.value && !currentPassword.value) {
    useAlert(t('AGENT_MGMT.ADD.API.STEP_UP_REQUIRED'));
    return;
  }

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
      password: agentPassword.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.role = 'agent';
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }
    if (showCurrentPassword.value) {
      payload.current_password = currentPassword.value;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(extractErrorMessage(error));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.HINT') }}
        </p>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentPassword.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.LABEL') }}
          <div class="relative">
            <input
              v-model="agentPassword"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              class="!pr-10"
              :placeholder="$t('AGENT_MGMT.ADD.FORM.PASSWORD.PLACEHOLDER')"
              @input="v$.agentPassword.$touch"
            />
            <button
              type="button"
              class="absolute top-1/2 -translate-y-1/2 ltr:right-2 rtl:left-2 p-1 text-n-slate-11 hover:text-n-slate-12"
              :aria-label="$t('AGENT_MGMT.ADD.FORM.PASSWORD.TOGGLE_VISIBILITY')"
              @click="showPassword = !showPassword"
            >
              <span
                :class="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                class="block size-4"
              />
            </button>
          </div>
          <span v-if="v$.agentPassword.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentPasswordConfirmation.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.LABEL') }}
          <div class="relative">
            <input
              v-model="agentPasswordConfirmation"
              :type="showPasswordConfirmation ? 'text' : 'password'"
              autocomplete="new-password"
              class="!pr-10"
              :placeholder="
                $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.PLACEHOLDER')
              "
              @input="v$.agentPasswordConfirmation.$touch"
            />
            <button
              type="button"
              class="absolute top-1/2 -translate-y-1/2 ltr:right-2 rtl:left-2 p-1 text-n-slate-11 hover:text-n-slate-12"
              :aria-label="
                $t(
                  'AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.TOGGLE_VISIBILITY'
                )
              "
              @click="showPasswordConfirmation = !showPasswordConfirmation"
            >
              <span
                :class="
                  showPasswordConfirmation ? 'i-lucide-eye-off' : 'i-lucide-eye'
                "
                class="block size-4"
              />
            </button>
          </div>
          <span v-if="v$.agentPasswordConfirmation.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.ERROR') }}
          </span>
        </label>
      </div>

      <div v-if="showCurrentPassword" class="w-full">
        <label>
          {{ $t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.LABEL') }}
          <input
            v-model="currentPassword"
            type="password"
            autocomplete="current-password"
            :placeholder="
              $t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.PLACEHOLDER')
            "
          />
        </label>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('AGENT_MGMT.ADD.FORM.CURRENT_PASSWORD_HINT') }}
        </p>
      </div>
      <p
        v-else-if="needsStepUp && !hasLocalPassword"
        class="w-full text-xs text-n-slate-11"
      >
        {{ $t('AGENT_MGMT.ADD.FORM.STEP_UP_SKIPPED') }}
      </p>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
